/**
 * @file useResourceInvalidate.js
 * @description This file contains the useResourceInvalidate hook that is used to invalidate queries in the react-query cache.
 * This file is copied from the refinedev-core package.
 * The main modification is add meta to the cache key to support multiple cache keys for the same resource.
 */
import { useCallback } from "react";

import {
  useQueryClient,
} from "@tanstack/react-query";

import { pickDataProvider, useKeys, useResource } from "@refinedev/core";

export const useResourceInvalidate = () => {
  const { resources } = useResource();
  const queryClient = useQueryClient();
  const { keys, preferLegacyKeys } = useKeys();

  const invalidate = useCallback(
    async ({
      resource,
      dataProviderName,
      invalidates,
      id,
      meta,
      invalidationFilters = { type: "all", refetchType: "active" },
      invalidationOptions = { cancelRefetch: false },
    }) => {
      if (invalidates === false) {
        return;
      }
      const dp = pickDataProvider(resource, dataProviderName, resources);

      const queryKey = keys()
        .data(dp)
        .resource(resource ?? "");

      await Promise.all(
        invalidates.map((key) => {
          switch (key) {
            case "all":
              return queryClient.invalidateQueries(
                keys().data(dp).get(preferLegacyKeys),
                invalidationFilters,
                invalidationOptions,
              );
            case "list":
              return queryClient.invalidateQueries(
                queryKey.action("list").get(preferLegacyKeys),
                invalidationFilters,
                invalidationOptions,
              );
            case "many":
              return queryClient.invalidateQueries(
                queryKey.action("many").get(preferLegacyKeys),
                invalidationFilters,
                invalidationOptions,
              );
            case "resourceAll":
              return queryClient.invalidateQueries(
                queryKey.get(preferLegacyKeys),
                invalidationFilters,
                invalidationOptions,
              );
            case "detail":
              return queryClient.invalidateQueries(
                queryKey
                  .action("one")
                  .id(id || "")
                  .params({
                    ...(meta || {}),
                  })
                  .get(preferLegacyKeys),
                invalidationFilters,
                invalidationOptions,
              );
            default:
              return;
          }
        }),
      );

      return;
    },
    [],
  );

  return invalidate;
};
